import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { ordenService, Orden } from '../../services/ordenService';
import { sucursalService } from '../../services/sucursalService';
import { useAuthStore } from '../../store/authStore';

const OrdenesList = () => {
  const [page, setPage] = useState(1);
  const [estadoFilter, setEstadoFilter] = useState<string>('pendiente');
  const [sucursalFilter, setSucursalFilter] = useState<number | undefined>(undefined);
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data, isLoading } = useQuery(
    ['ordenes', page, estadoFilter, sucursalFilter, fechaDesde, fechaHasta],
    () => ordenService.getAll(
      page,
      20,
      undefined,
      estadoFilter || undefined,
      sucursalFilter,
      fechaDesde || undefined,
      fechaHasta || undefined
    )
  );

  const procesarPagoMutation = useMutation(
    ({ id, total }: { id: number; total: number }) => ordenService.procesarPago(id, {
      pagos: [{
        tipoPago: 'efectivo',
        monto: total
      }]
    }),
    {
      onSuccess: () => {
        toast.success('Pago procesado correctamente');
        queryClient.invalidateQueries(['ordenes']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al procesar pago');
      }
    }
  );

  const cancelarMutation = useMutation(
    (id: number) => ordenService.cancelar(id),
    {
      onSuccess: () => {
        toast.success('Orden cancelada correctamente');
        queryClient.invalidateQueries(['ordenes']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al cancelar orden');
      }
    }
  );

  const handleProcesarPago = (orden: Orden) => {
    const total = Number(orden.total) || 0;
    if (window.confirm(`¿Procesar pago de la orden ${orden.numeroOrden || orden.id} por $${total.toFixed(2)}?`)) {
      procesarPagoMutation.mutate({ id: orden.id, total });
    }
  };

  const handleCancelar = (orden: Orden) => {
    const motivo = window.prompt('Motivo de cancelación (opcional):');
    if (motivo !== null) {
      ordenService.cancelar(orden.id, motivo || undefined).then(() => {
        toast.success('Orden cancelada correctamente');
        queryClient.invalidateQueries(['ordenes']);
      }).catch((error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al cancelar orden');
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  const estados = [
    { value: '', label: 'Todas' },
    { value: 'pendiente', label: 'Pendientes' },
    { value: 'en_proceso', label: 'En Proceso' },
    { value: 'completada', label: 'Completadas' },
    { value: 'cancelada', label: 'Canceladas' }
  ];

  const getEstadoColor = (estado: string) => {
    const colors: { [key: string]: string } = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      en_proceso: 'bg-blue-100 text-blue-800',
      completada: 'bg-green-100 text-green-800',
      cancelada: 'bg-red-100 text-red-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getEstadoLabel = (estado: string) => {
    const labels: { [key: string]: string } = {
      pendiente: 'Pendiente',
      en_proceso: 'En Proceso',
      completada: 'Completada',
      cancelada: 'Cancelada'
    };
    return labels[estado] || estado;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Órdenes / Ventas</h1>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={estadoFilter}
                onChange={(e) => {
                  setEstadoFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {estados.map((estado) => (
                  <option key={estado.value} value={estado.value}>
                    {estado.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sucursal
              </label>
              <select
                value={sucursalFilter || ''}
                onChange={(e) => {
                  setSucursalFilter(e.target.value ? Number(e.target.value) : undefined);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todas las sucursales</option>
                {sucursalesData?.data.sucursales.map((sucursal) => (
                  <option key={sucursal.id} value={sucursal.id}>
                    {sucursal.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha Desde
              </label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => {
                  setFechaDesde(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => {
                  setFechaHasta(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Número</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impuestos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.ordenes?.map((orden) => (
                <tr key={orden.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {orden.numeroOrden || `#${orden.id}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {orden.cliente?.nombre || 'Cliente General'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {orden.sucursal?.name || `Sucursal ${orden.sucursalId}` || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(orden.estado)}`}>
                      {getEstadoLabel(orden.estado)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${(Number(orden.subtotal) || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${(Number(orden.impuestos) || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    ${(Number(orden.total) || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {orden.createdAt ? new Date(orden.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    {orden.estado === 'pendiente' && (
                      <>
                        <button
                          onClick={() => handleProcesarPago(orden)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Procesar Pago
                        </button>
                        <button
                          onClick={() => handleCancelar(orden)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Cancelar
                        </button>
                      </>
                    )}
                    {orden.estado === 'en_proceso' && (
                      <button
                        onClick={() => handleProcesarPago(orden)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Procesar Pago
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(!data?.data.ordenes || data.data.ordenes.length === 0) && (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                    No hay órdenes para mostrar
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data?.data.pagination && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Mostrando {((page - 1) * 20) + 1} a {Math.min(page * 20, data.data.pagination.total)} de {data.data.pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= data.data.pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdenesList;

